import { siteApi } from "@api";
import { DepositDrawerSubScreen } from "@components/utils/DepositDrawer/types";
import { closeDeposit } from "@components/utils/DepositDrawer/utils";
import { closeIframeDialog } from "@components/utils/IframeDialog";
import { envConfig } from "@configs/env";
import { useStatsigTracking } from "@configs/statsig";
import { DepositIframeCloseReason } from "@configs/statsig/types";
import { AppDispatch } from "@stores";
import preferenceSlice from "@stores/preference/slices";
import logger from "@utils/logger";
import { fAmount, FundingStatus, FundingTxModel, FundingType, GetFundingResponseV2 } from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import useAuthSession from "./useAuthSession";
import { useDepositCompleteHandler } from "./useDepositCompleteHandler";
import usePreference from "./usePreference";
import useProfileBalance from "./useProfileBalance";

interface UseDepositStatusWatcherProps {
  onUpdate?: (fundingTx: FundingTxModel | null) => void;
  onCompleted?: (fundingTx: FundingTxModel) => void;
  pollingInterval?: number;
  pollingIntervalWithWs?: number;
  watcherTimeout?: number;
}

/**
 * WS Reconnect interval = 5000
 * Polling interval = 6000
 * After user back to the site, ws reconnecting should be run first
 * In case of ws reconnecting failed, then fallback to polling
 */
const DEFAULT_POLLING_INTERVAL = 6 * 1000; // 6s

// When run with ws, increase polling interval to 20s to prevent spamming the server
const DEFAULT_POLLING_INTERVAL_WITH_WS = 20 * 1000; // 20s

// Only run with ws for specific groups
const RUN_WITH_WS = ["phk1", "pkk1", "ph96g1", "phblg1", "bdk1"].includes(envConfig.brand.group);

// Auto-remove orderRef after 5 minutes to prevent infinite polling
const DEFAULT_WATCHER_TIMEOUT = 5 * 60 * 1000; // 5m

/**
 * Interval check deposit status from redux store
 * This fallback to polling if the websocket is not connected
 */
export const useDepositStatusWatcher = (options?: UseDepositStatusWatcherProps) => {
  const {
    onUpdate,
    onCompleted,
    pollingInterval = DEFAULT_POLLING_INTERVAL,
    pollingIntervalWithWs = DEFAULT_POLLING_INTERVAL_WITH_WS,
    watcherTimeout = DEFAULT_WATCHER_TIMEOUT,
  } = options || {};
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const { refetch: refetchBalance } = useProfileBalance();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuthSession();
  const { onComplete: onDepositComplete } = useDepositCompleteHandler();

  const { pendingDepositOrderRef: orderRef, isWsConnected } = usePreference();
  const dispatch = useDispatch<AppDispatch>();
  const [fundingResponse, setFundingResponse] = useState<GetFundingResponseV2>();
  const { trackDepositSuccess, trackCloseDepositCustom } = useStatsigTracking();
  const watcherTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // cache requestedAtQuery to trigger polling twice
  const requestedAtQuery = useMemo(() => {
    if (!orderRef) {
      return null;
    }
    const last24h = Math.floor(moment().subtract(24, "hours").valueOf() / 1000);
    return `${last24h},`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderRef]);

  const terminateWatcher = useCallback(() => {
    dispatch(preferenceSlice.actions.setPendingDepositOrderRef(null));
    if (watcherTimeoutRef.current) {
      clearTimeout(watcherTimeoutRef.current);
      watcherTimeoutRef.current = null;
    }
  }, [dispatch]);

  const handleOnCompleted = useCallback(
    (fundingTx: FundingTxModel) => {
      const toastId = `deposit-toast-${fundingTx?.orderRef}`;

      logger._console.log("[DepositStatusWatcher] handleOnCompleted called", {
        orderRef: fundingTx.orderRef,
        status: fundingTx.status,
        netAmount: fundingTx.netAmount,
        toastId,
      });

      onCompleted?.(fundingTx);

      // Track deposit success (polling fallback)
      trackDepositSuccess(Number(fundingTx?.netAmount) || 0, fundingTx?.orderRef, {
        currency: fundingTx?.reqCurrency || fundingTx?.netCurrency,
        method: fundingTx?.method ?? undefined,
        providerKey: fundingTx?.providerKey ?? undefined,
      });

      toast.success(t("Deposit amount completed", { amount: fAmount(fundingTx?.netAmount) }), {
        id: toastId,
      });

      closeIframeDialog({
        id: `deposit-${fundingTx?.orderRef}`,
        reason: DepositIframeCloseReason.DEPOSIT_SUCCESS,
      });
      // close deposit drawer
      closeDeposit({
        reason: DepositIframeCloseReason.DEPOSIT_SUCCESS,
        onTrackClose: (currentSubScreen?: DepositDrawerSubScreen) => {
          if (currentSubScreen === DepositDrawerSubScreen.NATIVE_DEPOSIT) {
            trackCloseDepositCustom({
              reason: DepositIframeCloseReason.DEPOSIT_SUCCESS,
              orderRef: fundingTx?.orderRef,
            });
          }
        },
      });
      setTimeout(() => {
        // BE needs time to update balance
        refetchBalance();
      }, 5000);

      // Refetch promotion statuses to update ineligible flow statuses
      onDepositComplete();
    },
    [onCompleted, refetchBalance, t, trackDepositSuccess, trackCloseDepositCustom, onDepositComplete],
  );

  const handleOnUpdate = useCallback(
    (res?: GetFundingResponseV2) => {
      const fundingTx = res?.entries?.[0];
      let shouldTerminateWatcher = false;

      // Skip processing if orderRef is null (watcher already terminated)
      if (!orderRef) {
        logger._console.log("[DepositStatusWatcher] orderRef is null, skipping processing");
        return;
      }

      if (fundingTx) {
        // Only process if orderRef matches
        if (fundingTx.orderRef !== orderRef) {
          logger._console.log("[DepositStatusWatcher] orderRef mismatch, skipping", {
            fundingTxOrderRef: fundingTx.orderRef,
            currentOrderRef: orderRef,
          });
          return;
        }

        onUpdate?.(fundingTx);
        if (fundingTx.status === FundingStatus.Completed) {
          logger._console.log("[DepositStatusWatcher] Status is Completed, calling handleOnCompleted", {
            orderRef: fundingTx.orderRef,
          });
          handleOnCompleted?.(fundingTx);
          shouldTerminateWatcher = true;
        }
        if ([FundingStatus.Error, FundingStatus.Cancelled, FundingStatus.Timeout].includes(fundingTx.status)) {
          logger._console.log("[DepositStatusWatcher] Status is error/cancelled/timeout, terminating", {
            orderRef: fundingTx.orderRef,
            status: fundingTx.status,
          });
          shouldTerminateWatcher = true;
        }
      } else {
        logger._console.log("[DepositStatusWatcher] No fundingTx found, terminating");
        shouldTerminateWatcher = true;
      }

      // Terminate watcher if orderRef is not found or status is error/cancelled/timeout
      if (shouldTerminateWatcher) {
        terminateWatcher();
      }
    },
    [onUpdate, handleOnCompleted, terminateWatcher, orderRef],
  );

  const shouldSkipPolling = useMemo(() => {
    // if orderRef is not set, skip polling
    if (!orderRef) {
      return true;
    }

    // if player is guest, skip polling
    if (!isAuthenticated) {
      return true;
    }

    // always run with ws if match group
    if (RUN_WITH_WS) {
      return false;
    }
    // if not, only run polling if ws is not connected
    return isWsConnected;
  }, [isWsConnected, isAuthenticated, orderRef]);

  // fallback to polling to fetch funding status when websocket is not connected
  const { data: pollingFundingRes, isFetching: isFetchingFundingRes } = siteApi.useGetFundingHistoryV2Query(
    buildFundingHistoryQuery(orderRef!, requestedAtQuery),
    {
      pollingInterval: isWsConnected ? pollingIntervalWithWs : pollingInterval,
      skip: shouldSkipPolling,
    },
  );

  // manually refetch funding status
  const [refetchFundingStatus] = siteApi.useLazyGetFundingHistoryV2Query();

  useEffect(() => {
    if (shouldRefetch && !!orderRef && !isFetchingFundingRes && isAuthenticated) {
      refetchFundingStatus(buildFundingHistoryQuery(orderRef, requestedAtQuery))
        .then((res) => {
          setFundingResponse(res.data);
        })
        .finally(() => {
          setShouldRefetch(false);
        });
    }
  }, [
    shouldRefetch,
    orderRef,
    isFetchingFundingRes,
    handleOnCompleted,
    onUpdate,
    refetchBalance,
    refetchFundingStatus,
    requestedAtQuery,
    isAuthenticated,
  ]);

  // Handle funding detail response
  useEffect(() => {
    setFundingResponse(pollingFundingRes);
  }, [pollingFundingRes]);

  // Clear fundingResponse when orderRef is cleared
  useEffect(() => {
    if (!orderRef) {
      logger._console.log("[DepositStatusWatcher] orderRef cleared, clearing fundingResponse");
      setFundingResponse(undefined);
    }
  }, [orderRef]);

  // trigger manual refetch when user returns to the site
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !!orderRef) {
        setShouldRefetch(true);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [orderRef]);

  // trigger manual refetch when ws is reconnected
  useEffect(() => {
    if (isWsConnected && !!orderRef) {
      setShouldRefetch(true);
    }
  }, [isWsConnected, orderRef]);

  // Reset shouldRefetch when orderRef changes
  useEffect(() => {
    setShouldRefetch(false);
  }, [orderRef]);

  // Auto terminate watcher to prevent infinite polling
  useEffect(() => {
    // Clear existing timeout when orderRef changed/removed
    if (watcherTimeoutRef.current) {
      clearTimeout(watcherTimeoutRef.current);
      watcherTimeoutRef.current = null;
    }

    // Set new timeout when orderRef is set
    if (!!orderRef) {
      watcherTimeoutRef.current = setTimeout(() => {
        terminateWatcher();
      }, watcherTimeout);
    }

    return () => {
      if (watcherTimeoutRef.current) {
        clearTimeout(watcherTimeoutRef.current);
        watcherTimeoutRef.current = null;
      }
    };
  }, [orderRef, terminateWatcher, watcherTimeout]);

  // Handle funding detail response - process fundingResponse
  useEffect(() => {
    // Skip if orderRef is null or fundingResponse doesn't exist
    if (!fundingResponse || !orderRef) {
      return;
    }

    handleOnUpdate(fundingResponse);
  }, [fundingResponse, handleOnUpdate, orderRef]);
};

const buildFundingHistoryQuery = (orderRef: string, timeStampParams: string | null) => {
  return {
    query: {
      limit: 1,
      offset: 0,
      orderRef: orderRef,
      type: FundingType.Deposit,
      requestedAt: timeStampParams,
    },
  };
};
